import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { PawPrint, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const [mode, setMode] = useState("login"); // login | register | code
  const [codeStep, setCodeStep] = useState("email"); // email | code
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { authenticate } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const finish = (data, msg) => { authenticate(data.token, data.user); toast.success(msg); navigate(from, { replace: true }); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(endpoint, payload);
      finish(data, mode === "login" ? "Welcome back" : "Account created");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    if (!form.email) { setError("Enter your email first"); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/request-code", { email: form.email });
      setCodeStep("code");
      toast.success("Code sent — check your email 🐾");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-code", { email: form.email, code });
      finish(data, "You're in 🐾");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 outline-none focus:border-primary transition-colors";

  return (
    <div className="max-w-md mx-auto px-5 sm:px-8 py-20" data-testid="auth-page">
      <div className="flex items-center gap-2 justify-center mb-8">
        <PawPrint weight="fill" className="text-primary" size={24} />
        <span className="font-modern text-xl font-semibold tracking-tight">MR<span className="text-primary">.</span>WIGGLEZ</span>
      </div>

      {mode !== "code" && (
        <div className="flex rounded-full border border-white/10 p-1 mb-8">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => { setMode(m); setError(""); }}
              className={`flex-1 py-2.5 rounded-full overline transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-white"}`}
              data-testid={`auth-tab-${m}`}>
              {m === "login" ? "Sign In" : "Create Account"}
            </button>
          ))}
        </div>
      )}

      {mode !== "code" && (
        <>
          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls} data-testid="auth-name" />
            )}
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls} data-testid="auth-email" />
            <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls} data-testid="auth-password" />

            {error && <p className="text-sm text-accent" data-testid="auth-error">{error}</p>}

            <button disabled={loading} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="auth-submit">
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" /><span className="overline text-muted-foreground text-[10px]">or the easy way</span><div className="h-px flex-1 bg-white/10" />
          </div>
          <button onClick={() => { setMode("code"); setCodeStep("email"); setError(""); setCode(""); }}
            className="w-full inline-flex items-center justify-center gap-2 border border-secondary/40 text-secondary py-3.5 rounded-full font-modern hover:bg-secondary/10 transition-colors" data-testid="auth-code-toggle">
            <EnvelopeSimple weight="fill" size={18} /> Email me a sign-in code (no password)
          </button>
          <p className="text-center text-xs text-muted-foreground mt-3">Forgot your password? Use the code — you'll be signed right in.</p>
        </>
      )}

      {mode === "code" && (
        <div data-testid="auth-code-panel">
          <button onClick={() => { setMode("login"); setError(""); }} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-white text-sm mb-5" data-testid="auth-code-back">
            <ArrowLeft size={16} /> Back to password sign-in
          </button>
          {codeStep === "email" ? (
            <form onSubmit={sendCode} className="space-y-4">
              <h2 className="font-display font-black uppercase text-2xl">No password needed</h2>
              <p className="text-sm text-muted-foreground">Enter your email and we'll send you a 6-digit code to sign in instantly. First time? This creates your account too.</p>
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls} data-testid="auth-code-email" autoFocus />
              {error && <p className="text-sm text-accent" data-testid="auth-error">{error}</p>}
              <button disabled={loading} className="w-full bg-secondary text-black py-3.5 rounded-full font-modern font-medium hover:opacity-90 transition-opacity disabled:opacity-60" data-testid="auth-code-send">
                {loading ? "Sending…" : "Send me a code"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyCode} className="space-y-4">
              <h2 className="font-display font-black uppercase text-2xl">Check your email</h2>
              <p className="text-sm text-muted-foreground">We sent a 6-digit code to <span className="text-white">{form.email}</span>. Enter it below.</p>
              <input required inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="6-digit code" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${inputCls} text-center text-2xl tracking-[0.5em] font-semibold`} data-testid="auth-code-input" autoFocus />
              {error && <p className="text-sm text-accent" data-testid="auth-error">{error}</p>}
              <button disabled={loading || code.length < 6} className="w-full bg-primary text-primary-foreground py-3.5 rounded-full font-modern font-medium hover:bg-primary/90 transition-colors disabled:opacity-60" data-testid="auth-code-verify">
                {loading ? "Verifying…" : "Verify & sign in"}
              </button>
              <button type="button" onClick={sendCode} disabled={loading} className="w-full text-sm text-secondary hover:underline" data-testid="auth-code-resend">
                Didn't get it? Resend code
              </button>
            </form>
          )}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-6">
        One account across web & mobile — Shop, Dog Gear, Collabs, Wrap and Mr Hub.
      </p>
    </div>
  );
}
